// Phase G3→G4 de-mock — persistence guard.
//
// Every real Supabase write from a ported screen is gated on this hook.
// It returns true ONLY when a real authenticated Supabase session
// exists. The dev-bypass user (`?dev=1` → useAuth stamps
// {id:"dev-local"} with no Supabase session) and unauthenticated
// visitors return false, so guarded writes become no-ops.
//
// Why this matters:
//   - e2e runs under the dev bypass → every guarded write is skipped,
//     so the suite never touches the network or the real DB.
//   - Stops prod pollution: e.g. the `waitlist` insert policy is
//     `WITH CHECK (true)` (anonymous-allowed); without this guard,
//     every e2e/dev run would write a real waitlist row. Real
//     unauthenticated visitors still hit that path in the browser
//     (the guard is false for them too, BUT the waitlist screen calls
//     it from a real page load where we DO want the insert — see
//     onboarding.kid.age, which checks the session directly rather
//     than skipping under dev bypass). For all student-/parent-scoped
//     writes the guard is the correct gate: no session ⇒ no write.
//
// Reads from pencilwise's REAL client (src/supabaseClient.js), never
// the @/integrations/supabase/client no-op mock.

import { useEffect, useState } from "react";
import { supabase } from "@/supabaseClient.js";

/**
 * True once a real Supabase auth session is confirmed present.
 * Starts false (fail-closed) and flips true only if getSession /
 * onAuthStateChange report a session. Use to gate real writes:
 *
 *   const canPersist = useRealSession();
 *   if (canPersist) await supabase.from(...).insert(...);
 */
export function useRealSession(): boolean {
  const [hasSession, setHasSession] = useState(false);

  useEffect(() => {
    let cancelled = false;
    void supabase.auth
      .getSession()
      .then(({ data }: { data: { session: unknown } }) => {
        if (!cancelled) setHasSession(!!data.session);
      });
    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange(
      (_event: unknown, session: unknown) => {
        setHasSession(!!session);
      },
    );
    return () => {
      cancelled = true;
      subscription?.unsubscribe?.();
    };
  }, []);

  return hasSession;
}

/**
 * Imperative one-shot check for non-React call sites (event handlers
 * that aren't inside a component render, or modules). Resolves to true
 * only when a real Supabase session exists.
 */
export async function hasRealSession(): Promise<boolean> {
  const { data } = await supabase.auth.getSession();
  return !!data.session;
}

/**
 * Pure core of the dev/e2e bypass check, exported for unit testing.
 *
 * import.meta.env is Vite-build-injected and unavailable (and not
 * per-module stubbable) under this repo's tsx-based unit-test harness
 * (see sentry.test.mjs's initSentry note for the identical constraint),
 * so the DEV flag and raw search string are threaded in explicitly here.
 * isDevBypass() below — the thin runtime wrapper every real call site
 * uses — is what actually reads import.meta.env.DEV + window.location.
 */
export function isDevBypassFromSearch(
  devBuild: boolean,
  search: string | null | undefined,
): boolean {
  if (!devBuild) return false;
  if (search == null) return false;
  try {
    return new URLSearchParams(search).get("dev") === "1";
  } catch {
    return false;
  }
}

/**
 * True when the app is running under the dev/e2e bypass (`?dev=1`,
 * matching useAuth's fake-user gate). DEV-gated on `import.meta.env.DEV`
 * so a deployed PRODUCTION build can never honor `?dev=1` — compiled out
 * of prod builds entirely, matching useAuth's fix for the same class of
 * bug (#59). `vite dev` (local dev) and the e2e harness (playwright.config.ts
 * boots via `npm run dev`, a real Vite dev server) both have DEV=true, so
 * `?dev=1` keeps working there unchanged.
 *
 * Used by the ONE intentionally anonymous write — the age-gate waitlist
 * insert (RLS `WITH CHECK (true)`). That write must keep working for real
 * unauthenticated visitors (who have no session, so useRealSession is
 * false for them too), but must be skipped under the dev bypass so
 * e2e/dev never pollute the real `waitlist` table.
 */
export function isDevBypass(): boolean {
  if (typeof window === "undefined") return false;
  return isDevBypassFromSearch(import.meta.env.DEV, window.location.search);
}
